/**
 * Error Utility Functions
 *
 * Error normalization and translation for authentication errors
 */

import type { AuthError } from '@/types';

/**
 * Cognito error name mappings for special cases
 */
const COGNITO_ERROR_MAPPINGS: Record<string, { name: string; condition: (message: string) => boolean }> = {
  /**
   * Cognito reports an expired temporary password as NotAuthorizedException with the message
   * "Temporary password has expired and must be reset by an administrator."
   *
   * Without this branch it fell through to the generic NotAuthorizedException mapping and the
   * user was told "Incorrect email or password" — so they retried their correct password
   * forever, and support had nothing to look at. Matched case-insensitively because the exact
   * casing of that sentence is not a contract.
   */
  TemporaryPasswordExpiredException: {
    name: 'TemporaryPasswordExpiredException',
    condition: (msg) => msg.toLowerCase().includes('temporary password has expired'),
  },
  InvalidSessionException: {
    name: 'InvalidSessionException',
    condition: (msg) => msg.includes('session is expired'),
  },
  EnableSoftwareTokenMFAException: {
    name: 'EnableSoftwareTokenMFAException',
    condition: (msg) => msg.includes('pattern: [0-9]'),
  },
  MFACodeMismatchException: {
    name: 'MFACodeMismatchException',
    condition: (msg) => msg.includes('Invalid code received for user'),
  },
};

/**
 * Normalize Cognito error to standard format
 * @param error - Error object from Cognito/Amplify
 * @returns Normalized error object
 */
export function normalizeCognitoError(error: unknown): { name: string; message: string } {
  if (typeof error !== 'object' || error === null) {
    return { name: 'UnknownError', message: '' };
  }

  const err = error as { name?: string; message?: string };
  const errorName = err.name || 'UnknownError';
  const errorMessage = err.message || '';

  // Check for special error mappings based on error name and message.
  // The temporary-password check MUST come first: both it and InvalidSessionException are
  // NotAuthorizedException, and this one is the more specific case.
  if (errorName === 'NotAuthorizedException' && COGNITO_ERROR_MAPPINGS.TemporaryPasswordExpiredException.condition(errorMessage)) {
    return { name: 'TemporaryPasswordExpiredException', message: errorMessage };
  }

  if (errorName === 'NotAuthorizedException' && COGNITO_ERROR_MAPPINGS.InvalidSessionException.condition(errorMessage)) {
    return { name: 'InvalidSessionException', message: errorMessage };
  }

  if (errorName === 'InvalidParameterException' && COGNITO_ERROR_MAPPINGS.EnableSoftwareTokenMFAException.condition(errorMessage)) {
    return { name: 'EnableSoftwareTokenMFAException', message: errorMessage };
  }

  if (errorName === 'CodeMismatchException' && COGNITO_ERROR_MAPPINGS.MFACodeMismatchException.condition(errorMessage)) {
    return { name: 'MFACodeMismatchException', message: errorMessage };
  }

  return {
    name: errorName,
    message: errorMessage,
  };
}

/**
 * Error code to i18n key mapping
 */
const ERROR_KEY_MAP: Record<string, string> = {
  UserNotConfirmedException: 'errors.UserNotConfirmedException',
  NotAuthorizedException: 'errors.NotAuthorizedException',
  TemporaryPasswordExpiredException: 'errors.TemporaryPasswordExpiredException',
  UserNotFoundException: 'errors.UserNotFoundException',
  InvalidSessionException: 'errors.InvalidSessionException',
  EnableSoftwareTokenMFAException: 'errors.EnableSoftwareTokenMFAException',
  InvalidParameterException: 'errors.InvalidParameterException',
  UnknownError: 'errors.DefaultError',
  EmptyVerifyTOTPSetupCode: 'errors.EmptyVerifyTOTPSetupCode',
  EmptyConfirmResetPasswordUsername: 'errors.EmptyConfirmResetPasswordUsername',
  CodeMismatchException: 'errors.CodeMismatchException',
  MFACodeMismatchException: 'errors.MFACodeMismatchException',
  PasswordHistoryPolicyViolationException: 'errors.PasswordHistoryPolicyViolationException',
  NetworkError: 'errors.NetworkError',
  TooManyRequestsException: 'errors.TooManyRequestsException',
  LimitExceededException: 'errors.LimitExceededException',
  ExpiredCodeException: 'errors.ExpiredCodeException',
  InvalidPasswordException: 'errors.InvalidPasswordException',
};

/**
 * Whether we have a real translation for this error code.
 *
 * `false` means the user is shown the generic "Login failed, please try again", which is both a
 * missing translation and a failure mode we do not yet understand — so it is worth escalating
 * rather than swallowing.
 */
export function isMappedErrorCode(code: string): boolean {
  return code in ERROR_KEY_MAP && ERROR_KEY_MAP[code] !== 'errors.DefaultError';
}

/**
 * Translate auth error to user-friendly message
 * @param error - Error object
 * @param t - Translation function
 * @returns Translated error message
 */
export function translateAuthError(error: unknown, t: (key: string) => string): string {
  const { name } = normalizeCognitoError(error);
  const i18nKey = ERROR_KEY_MAP[name] || 'errors.DefaultError';
  return t(i18nKey);
}

/**
 * Convert error to AuthError type
 * @param error - Error object
 * @returns Normalized AuthError object
 */
export function normalizeError(error: unknown): AuthError {
  const { name, message } = normalizeCognitoError(error);

  return {
    code: name,
    message: message || name,
    name,
    originalError: error,
  };
}

/**
 * Check if error is a network error
 * @param error - Error object
 * @returns true if network error
 */
export function isNetworkError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const err = error as { name?: string; message?: string };

  return (
    err.name === 'NetworkError' ||
    (err.message?.includes('Network') ?? false) ||
    (err.message?.includes('network') ?? false) ||
    (err.message?.includes('timeout') ?? false) ||
    (err.message?.includes('Failed to fetch') ?? false)
  );
}

/**
 * Check if error is a rate limit error
 * @param error - Error object
 * @returns true if rate limit error
 */
export function isRateLimitError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const err = error as { name?: string };

  return (
    err.name === 'TooManyRequestsException' ||
    err.name === 'LimitExceededException'
  );
}

/**
 * Check if error is an authentication error (wrong credentials)
 * @param error - Error object
 * @returns true if authentication error
 */
export function isAuthenticationError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const err = error as { name?: string };

  return (
    err.name === 'NotAuthorizedException' ||
    err.name === 'UserNotFoundException' ||
    err.name === 'InvalidPasswordException'
  );
}

/**
 * Get error recovery suggestion
 * @param error - Error object
 * @param t - Translation function
 * @returns Recovery suggestion text or null
 */
export function getErrorRecoverySuggestion(error: unknown, t: (key: string) => string): string | null {
  const { name } = normalizeCognitoError(error);

  const suggestions: Record<string, string> = {
    NetworkError: t('errors.suggestions.NetworkError'),
    TooManyRequestsException: t('errors.suggestions.TooManyRequestsException'),
    InvalidSessionException: t('errors.suggestions.InvalidSessionException'),
    UserNotFoundException: t('errors.suggestions.UserNotFoundException'),
  };

  return suggestions[name] || null;
}

/**
 * Format error for logging
 * @param error - Error object
 * @returns Formatted error string
 */
export function formatErrorForLogging(error: unknown): string {
  const normalized = normalizeError(error);
  return `[${normalized.code}] ${normalized.message}`;
}